import React from 'react';
import { useSync } from '@/hooks/useSync';
import { formatDistanceToNow } from 'date-fns';

interface SyncStatusProps {
  compact?: boolean;
}

const SyncStatus: React.FC<SyncStatusProps> = ({ compact = false }) => {
  const { status, error, lastSyncedAt, triggerSync } = useSync();

  const indicators: Record<string, { icon: React.ReactNode; label: string; color: string }> = {
    idle: {
      icon: (
        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
          <path d="M20 6 9 17l-5-5" />
        </svg>
      ),
      label: lastSyncedAt
        ? `Synced ${formatDistanceToNow(new Date(lastSyncedAt), { addSuffix: true })}`
        : 'Synced',
      color: 'text-green-500',
    },
    syncing: {
      icon: (
        <svg className="w-3.5 h-3.5 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
      ),
      label: 'Syncing…',
      color: 'text-blue-500',
    },
    error: {
      icon: (
        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="8" x2="12" y2="12" />
          <line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
      ),
      label: error ?? 'Sync error',
      color: 'text-red-500',
    },
    offline: {
      icon: (
        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <line x1="1" y1="1" x2="23" y2="23" />
          <path d="M16.72 11.06A10.94 10.94 0 0 1 19 12.55" />
          <path d="M5 12.55a10.94 10.94 0 0 1 5.17-2.39" />
          <path d="M10.71 5.05A16 16 0 0 1 22.56 9" />
          <path d="M1.42 9a15.91 15.91 0 0 1 4.7-2.88" />
          <path d="M8.53 16.11a6 6 0 0 1 6.95 0" />
          <line x1="12" y1="20" x2="12.01" y2="20" />
        </svg>
      ),
      label: 'Offline',
      color: 'text-yellow-500',
    },
  };

  const current = indicators[status] ?? indicators['idle'];

  if (compact) {
    return (
      <button
        type="button"
        onClick={triggerSync}
        title={current.label}
        className={`${current.color} transition-colors hover:opacity-80`}
        aria-label={`Sync status: ${current.label}`}
      >
        {current.icon}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={triggerSync}
      className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium border transition-all ${
        status === 'error'
          ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30'
          : status === 'offline'
          ? 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800 text-yellow-600 dark:text-yellow-400'
          : status === 'syncing'
          ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800 text-blue-600 dark:text-blue-400'
          : 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-green-600 dark:text-green-400 hover:bg-green-100'
      }`}
      aria-label={`Sync status: ${current.label}. Click to sync.`}
    >
      <span className={current.color}>{current.icon}</span>
      <span className="max-w-32 truncate">{current.label}</span>
    </button>
  );
};

export default SyncStatus;
